import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiService, Theme } from '../services/api';
import { useAuth } from './AuthContext';

interface ThemeContextType {
  currentTheme: Theme | null;
  availableThemes: Theme[];
  isLoading: boolean;
  updateTheme: (themeId: string, themeMode?: 'light' | 'dark' | 'system') => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

const hexToHslTuple = (hex: string): [number, number, number] => {
  const normalized = hex.replace('#', '').trim();
  if (normalized.length !== 6) return [0, 0, 100];

  const r = parseInt(normalized.slice(0, 2), 16) / 255;
  const g = parseInt(normalized.slice(2, 4), 16) / 255;
  const b = parseInt(normalized.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r:
        h = ((g - b) / delta) % 6;
        break;
      case g:
        h = (b - r) / delta + 2;
        break;
      default:
        h = (r - g) / delta + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  return [Math.round(h), Math.round(s * 100), Math.round(l * 100)];
};

const hexToShadcnHsl = (hex: string): string => {
  const [h, s, l] = hexToHslTuple(hex);
  return `${h} ${s}% ${l}%`;
};

const getContrastingHex = (hex: string): string => {
  // Relative luminance approximation for readable foreground choice
  const normalized = hex.replace('#', '').trim();
  if (normalized.length !== 6) return '#FFFFFF';
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.6 ? '#0B0B0B' : '#FFFFFF';
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { profile, refreshAuth } = useAuth();
  const [currentTheme, setCurrentTheme] = useState<Theme | null>(null);
  const [availableThemes, setAvailableThemes] = useState<Theme[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [systemIsDark, setSystemIsDark] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Apply CSS variables to document root
  const applyCSSVariables = useCallback((theme: Theme) => {
    const root = document.documentElement;
    
    // Core theme colors
    root.style.setProperty('--theme-primary', theme.primary_color);
    root.style.setProperty('--theme-secondary', theme.secondary_color);
    root.style.setProperty('--theme-accent', theme.accent_color);
    root.style.setProperty('--theme-background', theme.background_color);
    root.style.setProperty('--theme-surface', theme.surface_color);
    root.style.setProperty('--theme-text', theme.text_color);
    root.style.setProperty('--theme-text-secondary', theme.text_secondary_color);
    root.style.setProperty('--theme-expense', theme.expense_color);
    root.style.setProperty('--theme-income', theme.income_color);
    
    // Map to shadcn/tailwind color tokens so components like Calendar/Popover auto-theme
    // Tailwind config expects these as "H S% L%" strings, e.g. "0 0% 100%".
    root.style.setProperty('--background', hexToShadcnHsl(theme.background_color));
    root.style.setProperty('--foreground', hexToShadcnHsl(theme.text_color));
    root.style.setProperty('--card', hexToShadcnHsl(theme.surface_color));
    root.style.setProperty('--card-foreground', hexToShadcnHsl(theme.text_color));
    root.style.setProperty('--popover', hexToShadcnHsl(theme.surface_color));
    root.style.setProperty('--popover-foreground', hexToShadcnHsl(theme.text_color));

    // Use slightly muted surfaces; keep muted foreground aligned to theme's secondary text
    root.style.setProperty('--muted', hexToShadcnHsl(theme.surface_color));
    root.style.setProperty('--muted-foreground', hexToShadcnHsl(theme.text_secondary_color));

    // Accent is used for hover states; secondary color works well as an accent wash
    root.style.setProperty('--accent', hexToShadcnHsl(theme.secondary_color));
    root.style.setProperty('--accent-foreground', hexToShadcnHsl(theme.text_color));

    // Primary/Secondary tokens
    root.style.setProperty('--primary', hexToShadcnHsl(theme.primary_color));
    root.style.setProperty('--primary-foreground', hexToShadcnHsl(getContrastingHex(theme.primary_color)));
    root.style.setProperty('--secondary', hexToShadcnHsl(theme.secondary_color));
    root.style.setProperty('--secondary-foreground', hexToShadcnHsl(getContrastingHex(theme.secondary_color)));

    // Borders/inputs/rings
    root.style.setProperty('--border', hexToShadcnHsl(theme.text_secondary_color));
    root.style.setProperty('--input', hexToShadcnHsl(theme.text_secondary_color));
    root.style.setProperty('--ring', hexToShadcnHsl(theme.primary_color));

    // Apply dark/light class for Tailwind and shadcn
    if (theme.mode === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, []);

  // Load available themes
  useEffect(() => {
    const loadThemes = async () => {
      try {
        const data = await apiService.getThemes();
        setAvailableThemes(data.themes);
      } catch (error) {
        console.error('Failed to load themes:', error);
      }
    };

    loadThemes();
  }, []);

  // Load and apply user's theme when profile changes
  useEffect(() => {
    if (!profile || availableThemes.length === 0) return;

    const prefers: 'light' | 'dark' | 'system' = profile.theme_mode ?? 'system';
    const effectiveMode: 'light' | 'dark' = prefers === 'system' ? (systemIsDark ? 'dark' : 'light') : prefers;

    const resolveThemeIdForMode = (themeId: string, mode: 'light' | 'dark') => {
      const match = themeId.match(/-(light|dark)$/);
      if (!match) return themeId;
      return themeId.replace(/-(light|dark)$/, `-${mode}`);
    };

    const targetThemeId = resolveThemeIdForMode(profile.theme_id, effectiveMode);
    const userTheme = availableThemes.find(t => t.id === targetThemeId) || availableThemes.find(t => t.id === profile.theme_id);

    if (userTheme) {
      setCurrentTheme(userTheme);
      applyCSSVariables(userTheme);
    } else {
      // Fallback to default theme
      const defaultThemeId = prefers === 'system' ? `professional-${effectiveMode}` : 'professional-light';
      const defaultTheme = availableThemes.find(t => t.id === defaultThemeId) || availableThemes.find(t => t.id === 'professional-light');
      if (defaultTheme) {
        setCurrentTheme(defaultTheme);
        applyCSSVariables(defaultTheme);
      }
    }

    setIsLoading(false);
  }, [profile, availableThemes, applyCSSVariables, systemIsDark]);

  // React to OS theme changes when user preference is "system"
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    if (!profile || (profile.theme_mode ?? 'system') !== 'system') return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemIsDark(e.matches);

    // Init + subscribe
    setSystemIsDark(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    // Safari fallback
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, [profile]);

  const updateTheme = async (themeId: string, themeMode?: 'light' | 'dark' | 'system') => {
    try {
      // Find the theme from available themes
      const newTheme = availableThemes.find(t => t.id === themeId);
      if (!newTheme) {
        throw new Error('Theme not found');
      }

      // Update theme on backend
      await apiService.updateUserTheme(themeId, themeMode);
      
      // Apply CSS variables immediately
      setCurrentTheme(newTheme);
      applyCSSVariables(newTheme);

      // Refresh auth to get updated profile
      await refreshAuth();
    } catch (error) {
      console.error('Failed to update theme:', error);
      throw error;
    }
  };

  const value: ThemeContextType = {
    currentTheme,
    availableThemes,
    isLoading,
    updateTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

